<script lang="ts">
  import type { UserEvent } from '../../lib/pandalytics';
  import { getEventReport } from '../../lib/pandalytics';
  import {
    DataTable,
    DataTableRow,
    DataTableCell,
    DataTableHead,
    DataTableBody,
  } from 'svelte-materialify';
  import UsePreviewItem from '../item/UsePreviewItem.svelte';
  import { fromNow } from '../../lib/date';
  import { get } from 'lodash';
  import { UserService } from '../../modules';
  import type { User } from '../../modules/user/user.interface';
  import UserLink from './UserLink.svelte';

  interface FieldMapping {
    title: string;
    field: string;
    enabled?: boolean;
    transform?: (value: any) => any;
    numeric?: boolean;
  }

  type DistinctUser = { key: string } & Record<string, any>;

  const defaultFieldMappings: FieldMapping[] = [
    {
      title: 'User',
      field: 'customFields.userId',
    },
    {
      title: 'Event',
      field: 'action',
    },
    {
      title: 'Date',
      field: 'createdAt',
      transform: (dateString: string) => fromNow(dateString),
    },
    {
      title: 'Category',
      field: 'category',
    },
    {
      title: 'Label',
      field: 'label',
    },
    {
      title: 'Version',
      field: 'customFields.extensionVersion',
      numeric: true,
    },
    {
      title: 'Value',
      field: 'value',
      numeric: true,
    },
  ];

  export let events: UserEvent[];
  export let project: string;
  export let mappings: FieldMapping[] = [];

  let authUsers: User[] = [];
  let distinctUsers: DistinctUser[] = [];

  $: onEventsChange(events);
  $: fieldMappings = defaultFieldMappings.filter((fieldMapping) => {
    const userMapping = mappings.find((m) => m.field === fieldMapping.field);

    if (!userMapping) {
      return true;
    }

    return userMapping.enabled;
  });

  function isItemAction(action: string) {
    return ['BOOKMARK_ADD', 'BOOKMARK_DELETE', 'ITEM_CLICK'].includes(action);
  }

  async function onEventsChange(events: UserEvent[]) {
    authUsers = await UserService.getUsers(project);
    const { result } = await getEventReport({ project: 'ddr', name: 'distinctIdList' });
    distinctUsers = result;
  }

  function getLabel(event: UserEvent, fieldMapping: FieldMapping) {
    let value = get(event, fieldMapping.field);

    if (fieldMapping.transform) {
      value = fieldMapping.transform(value);
    }

    return value;
  }

  function getUser(event: UserEvent, authUsers: User[], distinctUsers: DistinctUser[]) {
    const { customFields, distinctId } = event;

    if (customFields?.userId) {
      const user = authUsers.find((user) => user.id === customFields.userId);

      if (user) {
        return {
          name: user.email || user.username || user.id,
          url: `/project/${project}/user/${user.id}`,
        };
      }
    }

    const distinctUser = distinctUsers.find(
      (distinctUser) => distinctUser.key === distinctId
    );

    const name =
      distinctUser?.['USER_NAME'] || distinctUser?.['USER_EMAIL'] || 'No name!';

    if (!distinctUser) {
      return {
        name,
      };
    }

    return {
      name,
      url: `/project/${project}/users/distinct/${distinctUser.key}`,
    };
  }
</script>

<DataTable>
  <DataTableHead>
    <DataTableRow>
      {#each fieldMappings as fieldMapping}
        <DataTableCell numeric={!!fieldMapping.numeric}
          >{fieldMapping.title}</DataTableCell
        >
      {/each}
    </DataTableRow>
  </DataTableHead>
  <DataTableBody>
    {#each events as event}
      <DataTableRow>
        {#each fieldMappings as fieldMapping}
          <DataTableCell numeric={!!fieldMapping.numeric}>
            {#if fieldMapping.title === 'User'}
              <UserLink user={getUser(event, authUsers, distinctUsers)} />
            {:else if fieldMapping.field === 'label' && isItemAction(event.action)}
              <UsePreviewItem {project} id={event.label} category={event.category}>
                {getLabel(event, fieldMapping)}
              </UsePreviewItem>
            {:else}
              {getLabel(event, fieldMapping) || ''}
            {/if}
          </DataTableCell>
        {/each}
      </DataTableRow>
    {/each}
  </DataTableBody>
</DataTable>
